import CustomLoadingButton from "@/components/Button/CustomLoadingButton";
import CardContainer from "@/components/Card/CardContainer";
import PermissionGuard, {
  PermissionGuardExternalProps,
} from "@/components/Guard/PermissionGuard";
import TableItemPlaceHolder from "@/components/Placeholder/TableItemPlaceHolder";
import {
  ITableButton,
  ITableHeaders,
  ITablePaginaion,
  SearchField,
  SelectedAction,
  TableRowData,
  TableToolTipProps,
} from "@/components/Table/table.interface";
import { BORDER_RADIUS, IS_DEV } from "@/constants";
import { messages } from "@/messages";
import { InputFieldType, Order } from "@/types";
import { sortRows } from "../sorting";
import { Button, SxProps, TablePagination, Theme } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableRow, { TableRowProps } from "@mui/material/TableRow";
import { styled } from "@mui/material/styles";
import { union, without } from "lodash";
import * as React from "react";
import {
  ActionColumnContext,
  ColumnsAreHideableContext,
  VisibleColumnsContext,
  useVisibleHeaders,
} from "../columnVisibility";
import ColumnPicker from "../ColumnPicker";
import {
  TableEditContext,
  TableEditingConfig,
  TableEditToggle,
  useTableEditing,
} from "../editing";
import TableViewToggle from "../TableViewToggle";
import useTablePreferences, {
  TABLE_VIEWS,
  TableCellDisplay,
  TableDensity,
  TableView,
} from "../tablePreferences";
import EnhancedTableHead from "./EnhancedTableHeader";
import EnhancedTableToolbar from "./EnhancedTableToolbar";

interface StyledTableRow extends TableRowProps {
  secondarytype?: "false" | "true";
}

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: theme.palette.common.black,
    boxShadow: "none",
    border: "none",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: "0.8125rem",
    border: "none",
  },
}));

export const StyledTableRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== "lastIndex",
})<StyledTableRow>(({ theme, secondarytype, onClick }) => ({
  borderRadius: "25px",
  "td:first-child": {
    borderTopLeftRadius: "10px",
    borderBottomLeftRadius: "10px",
  },
  "td:last-child": {
    borderBottomRightRadius: "10px",
    borderTopRightRadius: "10px",
  },
  backgroundColor:
    secondarytype === "true"
      ? theme.palette.background.paper
      : theme.palette.background.default,
  cursor: onClick ? "pointer" : "auto",
  "&:nth-of-type(odd)": {},
}));

export default function CustomTable<TDATA extends Record<string, any>>({
  title,
  buttonName,
  ButtonIcon,
  buttonFunction,
  buttonDisabled,
  buttonLoading = false,
  secondaryButtonName,
  deleteButtonName,
  deleteButtonFunction,
  isLoadingDelete,
  isLoadingAdd,
  addButtonName,
  addButtonFunction,
  secondaryButtonFunction,
  ButtonIconSecondary,
  tertiaryButtonName,
  TertiaryButtonIcon,
  tertiaryButtonFunction,
  tertiaryButtonDisabled,
  tertiaryButtonLoading,
  showButton = true,
  headers,
  rowData = [],
  tableRowComponent: TableRowComponent,
  tableMinimumWidth,
  buttonVariant = "custom1",
  pagination = true,
  isCheck = false,
  handleDelete,
  page = 0,
  handleChangePage = () => null,
  rowsPerPage = 10,
  handleChangeRowsPerPage = () => null,
  totalCount,
  tableRowComponentProps,
  type = "primary",
  isHeader = true,
  isLoading = false,
  sortByIndex = 0,
  sortBy = "asc",
  onSortChange,
  onRowClick,
  getRowKey,
  selectedActionObject,
  isClearSelected = false,
  setIsClearSelected,
  disableShadow = false,
  primaryButtonPermission,
  noOfItemsLoading,
  tableRowStyles,
  tableHeaderStyles = {},
  toolTipProps = {},
  isToolTip = false,
  cardStyles = {},
  searchField,
  emptyTableText,
  toolbarContent,
  stickyActions = true,
  cellDisplay: cellDisplayProp = "truncate",
  density: densityProp = "comfortable",
  maxLines: maxLinesProp,
  hiddenColumns,
  tableId,
  defaultView = "compact",
  columnPicker = false,
  editing,
  editPermission,
}: CustomTableProps<TDATA>) {
  const theme = useTheme();
  // Held in a ref inside the hook, so a keystroke re-renders one cell rather
  // than this component — which rebuilds `sx` as a fresh object literal below
  // and would have Emotion re-serialise every rule on every character. See the
  // note at the top of `../editing`.
  const edit = useTableEditing<any>(
    editing,
    rowData as any[],
    editing?.getRowId ?? ((row: any) => row?.id)
  );
  const isEditing = Boolean(edit?.isEditing);
  // Naming a `tableId` is what turns the three views on: it is what the choice
  // is remembered under, and a toggle that forgets itself on every navigation
  // would be worse than no toggle. Lists without one keep exactly the layout
  // they pass by hand, which is how the lab compares settings that aren't
  // shipping views.
  const [preferences, setPreferences] = useTablePreferences(tableId);
  const view = preferences.view ?? defaultView;
  const { cellDisplay, density, maxLines } = tableId
    ? TABLE_VIEWS[view]
    : {
        cellDisplay: cellDisplayProp,
        density: densityProp,
        maxLines: maxLinesProp,
      };
  // An action column that no button can appear in is dropped here — head and
  // cells together, `<ActionCell />` reading the same flag off context. Every
  // `headers` read below is on the filtered array; the one exception is the
  // `orderBy` seed just underneath, which indexes from the front while the action
  // column is last in every array that declares one.
  const { visibleHeaders: permittedHeaders, actionColumnVisible } =
    useVisibleHeaders(headers);
  // What may never be hidden. The action column because it is how you act on a
  // row; the `grow` column because hiding it makes `growIndex` -1, which sends
  // the table's slack back to the pinned action column and reinstates the
  // 260px-for-a-declared-160px bug. An invariant, not a nicety.
  // A column holding unsaved drafts joins that set for as long as the edit is
  // open. The picker is not reachable in edit mode, so this is not about the
  // user hiding it by hand — `useTablePreferences` re-reads storage whenever the
  // identity key changes, which happens on a `UserContext` refetch, and a stored
  // choice arriving mid-edit would take the column and its drafts off screen
  // while they still saved.
  const lockedColumnIds = React.useMemo(
    () =>
      permittedHeaders
        .filter(
          (header) =>
            header.grow ||
            /^actions?$/i.test(header.id) ||
            (isEditing && edit?.editableColumnIds.includes(header.id))
        )
        .map((header) => header.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // `grow` is in the key because the value is computed from it: an array that
    // moves its `grow` mark without changing the id list would otherwise keep
    // the previous lock set, leaving the new grow column unprotected.
    [
      permittedHeaders
        .map((header) => `${header.id}:${header.grow ? 1 : 0}`)
        .join("|"),
      isEditing,
      edit?.editableColumnIds.join("|"),
    ]
  );
  // The user's own column choice, applied on top of the permission filter. Both
  // narrow the same array so there is still exactly one answer to "which columns
  // is this table rendering", which is what `<Cell />` reads off context below.
  //
  // From the remembered preferences when this table renders the picker, from the
  // prop when the caller drives it by hand (the lab). Never both — two owners of
  // one list is how a picker ends up unable to restore a column it hid.
  const storedColumns =
    tableId && columnPicker ? preferences.hiddenColumns : hiddenColumns;
  /**
   * Columns a header marks `defaultHidden`, used ONLY as the opening state.
   *
   * Gated on the picker existing: without one there is no way to bring a column
   * back, so honouring the flag there would delete it outright rather than fold
   * it away.
   *
   * `??`, never `||` or a merge. `preferences.hiddenColumns` is `undefined`
   * until the user touches the picker and an ARRAY forever after — including the
   * empty array, which means "I want them all". Merging the seed in would make
   * that choice unreachable: showing a defaultHidden column would write `[]`,
   * and the next render would hide it again.
   */
  const seededHidden = React.useMemo(
    () =>
      tableId && columnPicker
        ? headers.filter((header) => header.defaultHidden).map((h) => h.id)
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tableId, columnPicker, headers.map((h) => `${h.id}:${h.defaultHidden ? 1 : 0}`).join("|")]
  );
  const chosenColumns = storedColumns ?? seededHidden;
  // Enforced HERE, where columns are actually dropped, and not only as a
  // disabled checkbox. The stored list is untrusted — `read()` checks its shape,
  // never that the ids are still unlocked — and a header array can gain or move
  // its `grow` mark in a later release, long after a user hid that column. Left
  // to the checkbox alone, such a record hides the locked column while the
  // picker draws it ticked and disabled, so there is no way to put it back.
  const visibleHeaders = chosenColumns?.length
    ? permittedHeaders.filter(
        (header) =>
          !chosenColumns.includes(header.id) ||
          lockedColumnIds.includes(header.id)
      )
    : permittedHeaders;
  // Capability, not current state — true while nothing is hidden yet, which is
  // when an unnamed `<Cell />` still has to be caught.
  const columnsAreHideable =
    Boolean(tableId && columnPicker) || Boolean(hiddenColumns);
  const visibleColumnIds = React.useMemo(
    () => new Set(visibleHeaders.map((header) => header.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleHeaders.map((header) => header.id).join("|")]
  );
  const [order, setOrder] = React.useState<Order>(sortBy);
  // Optional-chained, so `sortByIndex` may be -1 to open a list in the order the
  // API returned it — the same "no column is sorting" state the picker's Clear
  // sort produces, and what a list wants when its own order is the meaningful
  // one. It also stops an out-of-range index throwing: lists that compute it
  // with `findIndex` get -1 the moment that column is renamed or hidden, which
  // would otherwise take the whole table down on mount.
  const [orderBy, setOrderBy] = React.useState(headers[sortByIndex]?.id ?? "");
  // `orderBy` keeps sorting even when its own column is hidden, and that is the
  // right trade. An earlier version here suspended the sort instead, on the
  // grounds that every sort arrow lives on a header so hiding that header leaves
  // the rows in an order with nothing on screen to explain it. That reasoning
  // was checked against LINE_ITEMS_HEADERS, where 12 of 13 columns are `noSort`
  // and it costs nothing — and it is wrong anywhere else.
  //
  // Measured on Audience Manager: sort by Total Records ascending, hide that
  // column, and the rows snap straight back to raw API order — because
  // `getComparator(order, "")` reads `a[""]`, returns 0 for every pair, and
  // stableSort hands the array back untouched. The sort control was the label on
  // the header just hidden, so there is no way to ask for it again. Losing the
  // arrow is cosmetic; losing the order the user asked for is not.
  //
  // Nothing downstream needs the guard: EnhancedTableHead only ever compares
  // `orderBy === headCell.id`, so a hidden column simply matches nothing and no
  // header claims an arrow.
  // The table keeps sorting by a column the picker has hidden, on purpose —
  // dropping the order because someone removed a column they weren't reading is
  // the worse surprise. What that costs is any way to undo it, since the sort
  // label went with the column, so the picker is handed the header to offer it.
  const sortedHiddenColumn = React.useMemo(
    () =>
      orderBy && !visibleColumnIds.has(orderBy)
        ? permittedHeaders.find((header) => header.id === orderBy)
        : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orderBy, visibleColumnIds, permittedHeaders]
  );
  const [selected, setSelected] = React.useState<Array<any>>([]);
  const countRef = React.useRef<number | null>(null);
  const [isAllSelected, setIsAllSelected] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (
      rowData &&
      typeof rowData[0] === "object" &&
      "id" in rowData[0] &&
      selected.length > 0
    ) {
      const isSelected = rowData.every((x) => selected.includes(x.id));
      setIsAllSelected(isSelected);
    } else {
      if (isAllSelected) setIsAllSelected(false);
    }
  }, [rowData, selected]);

  React.useEffect(() => {
    if (isClearSelected) {
      setSelected([]);
      setIsClearSelected?.(false);
    }
  }, [isClearSelected]);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: any
  ) => {
    const isAsc = orderBy === property && order === "asc";
    const nextOrder = isAsc ? "desc" : "asc";
    setOrder(nextOrder);
    setOrderBy(property);
    // Told even when this table sorts locally, so a list can persist the choice
    // or reset its pagination without also having to own the sort state.
    onSortChange?.({ columnId: property, order: nextOrder });
  };

  // The header the sort reads through. Resolved off `permittedHeaders` rather
  // than `visibleHeaders` because sorting deliberately survives the picker
  // hiding its own column — see the `orderBy` note above.
  const sortHeader = React.useMemo(
    () => permittedHeaders.find((header) => header.id === orderBy),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orderBy, permittedHeaders]
  );

  // Sorting the rows the table was handed sorts the CURRENT PAGE. That is the
  // whole result set on a list that loads everything, and 10 of 4,000 rows on a
  // server-paginated one — where reordering the page under a control that reads
  // as global is worse than not offering it. A list in that position either
  // marks its columns `noSort` or passes `onSortChange` and sorts server-side;
  // passing it switches this off rather than sorting twice by different rules.
  const sortedRows = React.useMemo(
    () => (onSortChange ? rowData : sortRows(rowData, sortHeader, order)),
    [rowData, sortHeader, order, onSortChange]
  );

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = union(
        selected,
        rowData.map((n) => n.id)
      );
      setSelected(newSelected);
      return;
    }
    setSelected(without(selected, ...rowData.map((n) => n.id)));
  };

  const handleClickCheckBox = (
    event: React.MouseEvent<unknown>,
    name: string | number
  ) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected: Array<any> = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  if (totalCount != null && countRef.current !== totalCount) {
    countRef.current = totalCount;
  }

  // Rows that render a full-width placeholder of their own take `headerLength`
  // from the caller, and every caller computes it from its own unfiltered header
  // array — so the span is already one too many wherever the action column is
  // permission-dropped, and the column picker will make it wronger. The caller
  // cannot see the filtered array; this can, so correct it here rather than
  // threading a second prop through eight call sites.
  const rowComponentProps = React.useMemo(
    () =>
      tableRowComponentProps && "headerLength" in tableRowComponentProps
        ? { ...tableRowComponentProps, headerLength: visibleHeaders.length }
        : tableRowComponentProps,
    [tableRowComponentProps, visibleHeaders.length]
  );

  // Selection is suppressed for as long as the edit is open, rather than cleared
  // once on entry. Clearing once is not enough: the checkboxes are rendered by
  // the caller's row, not by this component, so a later click would put
  // `numSelected` above zero, flip the toolbar to its bulk-action branch, and
  // take Save off screen with drafts still pending — recoverable only by
  // noticing that unticking a row brings it back.
  const onCheckTableRowProps: any = isCheck
    ? {
        handleClickCheckBox: isEditing ? () => null : handleClickCheckBox,
        selected: isEditing ? [] : selected,
      }
    : null;
  const numSelected = isEditing ? 0 : selected.length;

  // A header cell may span more than one column via `colSpan`, which shifts
  // every subsequent cell out of step with its header. Column-position rules
  // would then land on the wrong cell, so opt the whole table out rather than
  // guess. Callers rarely hand back a stable `headers` array — ListAdvertiser
  // and the Audience lists build theirs from a function call during render.
  // Keying the layout work on the array's *contents* rather than its identity
  // stops it re-running on every render.
  //
  // Built from `visibleHeaders`, not `headers`: dropping the action column has to
  // change this string, or every memo and the effect below would keep running
  // against a column that is no longer rendered. Permissions can change without
  // the table unmounting — UserContext swaps the user object on refetch — so this
  // is the only thing that carries that change downstream.
  const headerKey = visibleHeaders
    .map(
      (header: any) =>
        // Every header field `layoutSx` branches on, `wrap` and `grow`
        // included — this is its only content dep, so a flag missing here is a
        // table rendering the previous array's CSS until some unrelated id or
        // width happens to change.
        `${header.id}:${header.width ?? ""}:${header.noTruncate ? 1 : 0}:${
          header.colSpan ?? 1
        }:${header.wrap ? 1 : 0}:${header.grow ? 1 : 0}:${
          // `editable` earns its place here the same way the others did: the
          // clamp branch below reads it, so an array that gains the mark without
          // changing any id or width would leave this table rendering the
          // previous array's CSS — with the clamp still on the cell holding an
          // input.
          header.editable ? 1 : 0
        }`
    )
    .join("|");

  const hasUniformColumns = React.useMemo(
    () => visibleHeaders.every((header: any) => (header.colSpan ?? 1) === 1),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [headerKey]
  );

  // Which column holds the actions, resolved from the runtime headers rather
  // than assumed to be last. Deliberately independent of `stickyActions`: the
  // truncation exemption below needs it even when pinning is switched off,
  // and folding the two together meant opting out of pinning also clipped the
  // action buttons out of reach.
  const actionColumnIndex = React.useMemo(() => {
    if (!hasUniformColumns) return -1;
    return visibleHeaders.findIndex((header) => /^actions?$/i.test(header.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerKey, hasUniformColumns]);

  // 1-based position for nth-of-type, and only when the column is pinned.
  const actionColumnPosition =
    stickyActions && actionColumnIndex !== -1 ? actionColumnIndex + 1 : -1;

  // Widths are only authoritative under `table-layout: fixed`, and that is only
  // safe once every column declares one — otherwise undeclared columns would
  // silently split the remainder.
  const columnWidths = React.useMemo(() => {
    if (!hasUniformColumns) return null;
    if (!visibleHeaders.every((header) => header.width != null)) return null;
    return visibleHeaders.map((header) => header.width as string | number);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerKey, hasUniformColumns]);

  // Whether `layoutSx` emits anything at all. The scroll/resize effect below is
  // only worth running when it does, and the two used to be kept in step by hand
  // — one derived flag instead, so adding a branch can't leave the effect behind.
  const emitsLayoutRules =
    cellDisplay === "fit" ||
    Boolean(columnWidths) ||
    actionColumnPosition > 0 ||
    density === "compact" ||
    Boolean(maxLines);

  const layoutSx = React.useMemo(() => {
    const sx: Record<string, any> = {};

    if (isEditing) {
      // Rows stand their own buttons down while editing — they have to, since
      // this component cannot reach controls the caller renders. But MUI's
      // disabled state only recolours things it owns, and the action icons in
      // this app are raw `<svg>` with a hardcoded `fill` (#050f27 for edit,
      // #A90016 for delete). Disabled, they still render at full strength: a
      // Delete button that looks completely live and does nothing when clicked.
      // Dimming is the only signal that reaches them.
      sx["&& > tbody td .MuiIconButton-root.Mui-disabled"] = { opacity: 0.35 };
      sx["&& > tbody td .MuiButton-root.Mui-disabled"] = { opacity: 0.35 };
    }

    // How a cell handles content wider than its column — see the prop's docs for
    // how the three named views map onto this.
    if (cellDisplay === "fit") {
      // Columns size to their content and the table outgrows its card, scrolling
      // horizontally with the action column pinned. Declared widths are ignored
      // outright here: under `table-layout: auto` they are hints the browser is
      // free to overrule, and pretending otherwise is what made widths
      // all-or-nothing in the first place.
      const ok = '&[data-columns-match="true"]';
      sx[ok] = {
        tableLayout: "auto",
        width: "max-content",
        minWidth: "100%",
        maxWidth: "none",
      };
      sx[`${ok} > thead th`] = { whiteSpace: "nowrap" };
      sx[`${ok} > tbody td:not([colspan])`] = { whiteSpace: "nowrap" };
      // An input has an intrinsic width of its own — around 180px — and under
      // `table-layout: auto` that is what the column sizes to, ignoring the
      // declared width like everything else here does. The table would jump
      // wider the moment edit mode opened, and since the horizontal scroll
      // position is kept, the user would be left looking at a different column.
      // So the edited columns are the one thing in this view that IS pinned.
      if (isEditing) {
        visibleHeaders.forEach((header, index) => {
          if (!header.editable || header.width == null) return;
          const at = `:nth-of-type(${index + 1})`;
          sx[`${ok} > thead th${at}`] = { width: header.width };
          sx[`${ok} > tbody td${at}:not([colspan])`] = {
            width: header.width,
            whiteSpace: "nowrap",
          };
        });
      }
    } else if (columnWidths) {
      const totalWidth = columnWidths.reduce<number>(
        (sum, width) =>
          sum + (typeof width === "number" ? width : parseFloat(String(width))),
        0
      );
      const allPx = columnWidths.every((width) => typeof width === "number");
      // Every rule below is gated on the rendered cells matching the headers.
      // `sync()` clears this when they don't. Permission filtering used to be
      // the usual cause and no longer exists, but rows that vary their own cell
      // count still diverge — Contact drops a cell for records with no
      // organization, and BeeswaxList and the LeadAppend detail table are
      // permanently one cell out. Without the gate those tables would leave a
      // column unsized under fixed layout, collapsing it to zero width.
      const ok = '&[data-columns-match="true"]';
      sx[ok] = { tableLayout: "fixed" };
      if (allPx) {
        // Declared widths are a floor, not a fixed size: the table still fills
        // its card when there is room to spare, and only once it would shrink
        // below them does it scroll — with the action column pinned.
        sx[ok] = {
          ...sx[ok],
          width: "100%",
          minWidth: totalWidth,
          maxWidth: "none",
        };
      }
      // One column may take the slack when the table is wider than its columns
      // add up to. Under `table-layout: fixed` an `auto` column collects the
      // remainder, and the table's `min-width` is the declared total, so at the
      // narrowest it still gets exactly its declared width. Left unmarked, the
      // remainder falls to the last column — the pinned actions — which then
      // renders far wider than it asked for.
      const growIndex = visibleHeaders.findIndex((header) => header.grow);
      columnWidths.forEach((width, index) => {
        sx[`${ok} > thead th:nth-of-type(${index + 1})`] = {
          width: index === growIndex ? "auto" : width,
        };
      });
      visibleHeaders.forEach((header, index) => {
        if (header.noTruncate || index === actionColumnIndex) return;
        // A column being edited holds a field, and every rule in this block
        // exists to clip TEXT. `overflow: hidden` on the wrapper takes the focus
        // ring and the error adornment with it, and `text-overflow: ellipsis`
        // means nothing to an input. So the column opts out for as long as the
        // edit is open, and goes back to normal the moment it closes.
        const editingThisColumn = isEditing && Boolean(header.editable);
        // Wrapping is opt-in per column. Applied to everything it turns narrow
        // value columns into mid-word rubble ("Under-" / "deliveri" / "ng"),
        // which is why the design it came from wraps the name column only.
        const truncate = editingThisColumn
          ? {
              overflow: "visible",
              textOverflow: "clip",
              whiteSpace: "normal",
            }
          : cellDisplay === "wrap" && header.wrap
            ? {
                // Keeps the declared widths and the fixed layout, and spends the
                // extra content on height instead of hiding it. `anywhere` rather
                // than `break-word` because the values that overflow here are
                // unbroken tokens — `e2e-R-C2-banner-cpmpaced-freq` has nowhere
                // to break on whitespace.
                whiteSpace: "normal",
                overflowWrap: "anywhere",
                overflow: "visible",
                textOverflow: "clip",
              }
            : {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              };
        // `:not([colspan])` keeps the loading placeholder, the empty state and
        // per-row loading branches — which span the full row — unaffected.
        const cell = `${ok} > tbody td:nth-of-type(${
          index + 1
        }):not([colspan])`;
        sx[cell] = truncate;
        // Rows often wrap their text in a Box, and text-overflow does not reach
        // block descendants. `min-width: 0` is what actually lets a flex child
        // shrink below its content; without it the rule is inert on the flex
        // wrappers several rows use. Cells laying out several elements side by
        // side still can't ellipsise — those columns want `noTruncate`.
        const shrink = { ...truncate, maxWidth: "100%", minWidth: 0 };
        sx[`${cell} > *:only-child`] = shrink;
        // Two hops, because `<Cell />` puts the row's own element one level
        // deeper — `td > span[data-cell-content] > <the row's Box>`. Without the
        // second hop a converted row loses both the shrink and the ellipsis:
        // Creatives' `vendorApproval` renders `<CustomIconPopover />` as an
        // element-only child, which would clip with no ellipsis at all.
        sx[`${cell} > span[data-cell-content] > *:only-child`] = shrink;
        // The wrapper itself. Addressed by tag *and* attribute so it outranks
        // the `*:only-child` rule above whatever order Emotion emits them in.
        //
        // It needs a block display before any of this applies — it is a <span>,
        // and an inline box ignores overflow entirely. That display belongs here
        // rather than on `*:only-child`, because forcing it on an arbitrary
        // element would flatten the `display: flex` wrappers unconverted rows
        // use.
        //
        // It is also the only element the clamp may land on, being the one
        // element in the cell that CustomTable controls. The clamp is merged in
        // rather than emitted as a second, competing rule — both would select
        // this element at the same specificity, and which won came down to
        // emission order, with the wrap rule's `overflow: visible` beating the
        // clamp's `overflow: hidden`. That left a clamp declaring a line count
        // and doing nothing. One rule cannot disagree with itself.
        //
        // While this column is being edited the clamp is REPLACED, not overridden
        // by a second rule. Both facts are known here, so there is no reason to
        // emit a competing selector — and a competing one would lose: at equal
        // specificity the winner is whichever Emotion happens to serialise last,
        // which is exactly the trap described above.
        //
        // This is not a nicety. Creatives opens in the Full view
        // (`cellDisplay: "wrap"`, `maxLines: 3`), so without this the input
        // renders inside a `-webkit-box` with `overflow: hidden` in the DEFAULT
        // view — clipping the focus ring, and hiding the error adornment
        // outright, so Save sits disabled with nothing on screen saying why.
        const clamp =
          editingThisColumn
            ? { display: "block", overflow: "visible" }
            : maxLines && cellDisplay === "wrap" && header.wrap
            ? {
                display: "-webkit-box",
                WebkitLineClamp: String(maxLines),
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }
            : { display: "block" };
        sx[`${cell} > span[data-cell-content]`] = { ...shrink, ...clamp };
      });
    }

    if (actionColumnPosition > 0) {
      // The surface the table sits on: the header row and the gaps between rows
      // are transparent, so both fall through to CardContainer.
      const surface =
        type === "secondary"
          ? theme.palette.background.default
          : theme.palette.background.paper;
      const sticky = { position: "sticky", right: 0 };
      // Rows are separated by 16px of transparent border-spacing. Left alone, the
      // scrolling columns show through those gaps beside the pinned cells, so the
      // pinned area reads as a striped band instead of a solid column. Painting
      // the gap above and below each cell closes it up.
      const closeRowGaps = `0 8px 0 0 ${surface}, 0 -8px 0 0 ${surface}`;

      // Gated on the same reconciliation as the widths: pinning selects a cell by
      // column position, which is only meaningful while every row renders the
      // same number of cells. Contact varies its cell count per row, so pinning
      // there would grab a data cell on some rows and the actions on others.
      const ok = '&[data-columns-match="true"]';

      // Pinned at every breakpoint. It costs ~154px, which is a lot of a phone
      // viewport, but small screens are exactly where the table scrolls furthest:
      // unpinned, scrolling to the actions leaves every row reading "0 / N/A /
      // buttons" with nothing to identify which row is about to be deleted.
      // Tables that genuinely need it off pass `stickyActions={false}`.
      sx[
        `${ok} > tbody td:nth-of-type(${actionColumnPosition}):not([colspan])`
      ] = {
        ...sticky,
        zIndex: 2,
        // Tracks StyledTableRow, so `type="secondary"` and any future row hover
        // colour follow automatically.
        backgroundColor: "inherit",
        boxShadow: closeRowGaps,
      };
      // Merged, not assigned: the width loop above writes this exact selector,
      // and overwriting it left the action column with no width at all. Under
      // fixed layout that makes it an `auto` column, so it collected the table's
      // leftover space and rendered at 260px for a declared 160px — with the
      // buttons stranded at the left of it.
      sx[`${ok} > thead th:nth-of-type(${actionColumnPosition})`] = {
        ...sx[`${ok} > thead th:nth-of-type(${actionColumnPosition})`],
        ...sticky,
        // Above the body so cells pass under it at the intersection.
        zIndex: 3,
        // The header row has no background of its own — what looks like one is
        // CardContainer showing through — so it must be set explicitly or the
        // body would scroll straight through the pinned header cell.
        backgroundColor: surface,
      };
    }

    if (density === "compact") {
      // Smaller type and tighter cells, so more columns fit before anything has
      // to be hidden or clipped. `&&` doubles the root specificity: StyledTableCell
      // sets its font on `&.MuiTableCell-body`, which a plain descendant selector
      // loses to.
      sx["&& > tbody td"] = { padding: "6px 10px", fontSize: "0.75rem" };
      sx["&& > thead th"] = { padding: "6px 10px", fontSize: "0.75rem" };

      // Icons have to come down with the type, and none of them follow it on
      // their own. Three different shapes are in these cells and only one of
      // them is even sized in CSS:
      //
      //   - the action icons are raw `<svg width="24" height="24">` with no
      //     class at all, so `font-size` does nothing to them;
      //   - channel logos are raw `<svg width="25">` sitting straight in a cell,
      //     outside any button;
      //   - vendor approval is a real `<SvgIcon>`, which font-size would size.
      //
      // Width and height cover all three: an SVG's `width`/`height` attributes
      // are presentational hints that any CSS rule outranks, and on `<SvgIcon>`
      // they override its em-based sizing to the same 20px.
      //
      // `<img>` is deliberately untouched — the Creatives thumbnail is content,
      // not an icon, and shrinking it would make the artwork unreadable. So is
      // the spinner inside a loading button, which sizes itself.
      const compactIcon = "1.25rem";
      sx["&& > tbody td svg:not(.MuiCircularProgress-svg)"] = {
        width: compactIcon,
        height: compactIcon,
      };
      // The button box, not just its icon. This is what actually sets the row
      // height: 24px icon + 8px padding either side is a 40px button, and with
      // 6px of cell padding that is the 52px row Compact was still rendering —
      // identical to Full, so the view saved nothing vertically. 5px padding
      // around a 20px icon is a 30px button, which is MUI's own `size="small"`.
      sx["&& > tbody td .MuiIconButton-root"] = { padding: "5px" };
      // Not every action column is icons. Audience Manager's is text buttons,
      // which followed density even less than the icons did — a 14px button
      // inside a 12px row, and a row height that did not move at all between
      // the two views. Scoped to cells so the toolbar's Create button, which is
      // not part of the density, keeps its size.
      // `minHeight` as well as padding: the `custom1` variant pins 35px
      // (theme/overridesComponets/button/button.ts), so shrinking the type alone
      // left the button — and therefore the row — exactly the height it was.
      sx["&& > tbody td .MuiButton-root"] = {
        padding: "2px 10px",
        fontSize: "0.75rem",
        minHeight: "28px",
      };
      // Fields, for the same reason and by the same means. Nothing above reaches
      // them: an input is neither an svg nor a button, and MUI sizes
      // `size="small"` from its own padding rather than from `font-size`. Left
      // out, a 40px field sets the height of a row the rest of this branch has
      // brought down to ~30px — so Compact would render TALLER than Full the
      // moment edit mode opened, and the view would be saying the opposite of
      // what it does.
      // `minHeight`, not padding — the theme pins this element at 44px, which
      // padding does not budge and which is taller than the whole row.
      sx["&& > tbody td .MuiOutlinedInput-input"] = {
        padding: "0 8px",
        minHeight: "26px",
        fontSize: "0.75rem",
      };
      // The adornment icon rides the type down too — it is an `<SvgIcon>`, so
      // the svg rule above already sized it, but its button-sized padding is
      // what would keep the row tall.
      sx["&& > tbody td .MuiInputAdornment-root"] = { marginLeft: "2px" };
      // Header icons too, or a 24px tooltip glyph sits next to 12px text. The
      // sort arrow is excluded: MUI already sizes it at 18px, so this would
      // make it BIGGER.
      sx["&& > thead th .MuiSvgIcon-root:not(.MuiTableSortLabel-icon)"] = {
        fontSize: compactIcon,
      };
    }

    return sx;
    // Keyed on header contents, not identity — a new object here means Emotion
    // re-serialises ~26 selectors on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    columnWidths,
    headerKey,
    actionColumnPosition,
    type,
    theme,
    cellDisplay,
    density,
    maxLines,
    // Three branches read it — the truncation opt-out, the clamp, and the fit
    // width pin. Without it here, turning edit mode on changes nothing visible
    // until some unrelated width or id happens to move.
    isEditing,
  ]);

  // Clipped cells reveal their full value through the app-level
  // <OverflowTooltip />, so nothing here observes or measures its own cells.
  const tableRef = React.useRef<HTMLTableElement>(null);

  // Drives the pinned column's leading edge. Both values are written to the DOM
  // rather than held in state, because this runs on every resize and scroll and
  // re-rendering the whole table for a shadow would be wasteful.
  const scrollWrapperRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    // Everything below feeds `layoutSx`, and for a table whose `layoutSx` is
    // empty all four DOM writes here are read by nobody — so skip the observer
    // rather than run a row walk per resize frame for output nothing consumes.
    // `emitsLayoutRules` is derived from the same conditions layoutSx branches
    // on, so a new branch can no longer leave this guard behind.
    if (!emitsLayoutRules) return;

    const table = tableRef.current;
    const scroller = table?.parentElement;
    const wrapper = scrollWrapperRef.current;
    if (!table || !scroller || !wrapper) return;

    // Writing to the DOM invalidates style, so a blind write on every callback
    // would force this table to lay out again on the very next read. While the
    // nav drawer animates, the container resizes on every frame — that turned a
    // 225ms transition into a frame-by-frame relayout of every cell. Both values
    // are stable across an animation, so only write when they actually change.
    let lastWidth: string | undefined;
    let lastOverflowing: string | undefined;
    let lastHeaderOffset: string | undefined;
    let lastColumnsMatch: string | undefined;

    const sync = () => {
      // The edge sits at the pinned column's inner boundary, so it has to track
      // that column's rendered width. Tables rendered with `isHeader={false}`
      // have no thead at all, so fall back to the body — otherwise the width
      // resolves to 0 and the divider is drawn at the container edge, straight
      // through the action buttons.
      const actionCell =
        table.querySelector<HTMLElement>(
          `thead th:nth-of-type(${actionColumnPosition})`
        ) ??
        table.querySelector<HTMLElement>(
          `tbody tr:first-of-type td:nth-of-type(${actionColumnPosition})`
        );
      const width = `${actionCell?.offsetWidth ?? 0}px`;
      const overflowing = String(scroller.scrollWidth > scroller.clientWidth);
      // Where the header starts inside the scroll area. The edge begins there so
      // it doesn't start in the padding above, which showed as a visible notch.
      const headerOffset = `${table.querySelector("thead")?.offsetTop ?? 0}px`;

      // Column-position rules assume the header manifest describes every
      // rendered cell. Compare against the manifest rather than the rendered
      // <th>s: tables passed `isHeader={false}` render no <th> at all, so the
      // DOM count was 0 and short-circuited this to "matched", disabling the
      // guard on exactly the tables whose rows vary their own cell count.
      // Counted by colSpan, since a spanning header covers several columns.
      const expectedColumns = visibleHeaders.reduce(
        (total: number, header: any) => total + (header.colSpan ?? 1),
        0
      );
      // Every row, not just the first: some rows render a cell conditionally on
      // their own data (Contact omits one when the record has no organization),
      // so the count can vary row to row and sampling one would miss it.
      // Loading placeholders and the empty state span the row with colspan, so
      // they contribute 0 and leave the optimistic default in place until real
      // rows arrive — at which point the row count changes and sync runs again.
      const rowCellCounts = Array.from(table.querySelectorAll("tbody tr"))
        .map(
          (row) =>
            Array.from(row.querySelectorAll(":scope > td")).filter(
              (cell) => !cell.hasAttribute("colspan")
            ).length
        )
        .filter((count) => count > 0);
      const columnsMatch = String(
        expectedColumns === 0 ||
          rowCellCounts.length === 0 ||
          rowCellCounts.every((count) => count === expectedColumns)
      );

      // A mismatch silently switches this table back to auto layout with no
      // truncation and no pinned column — no error, nothing in the UI that says
      // why. The usual cause is a row and its header array drifting apart, which
      // is easy to do when an action column is declared on one and its cell
      // wasn't converted to <ActionCell /> on the other. Say so while developing
      // rather than leaving it to be noticed months later.
      if (
        IS_DEV &&
        columnsMatch === "false" &&
        columnsMatch !== lastColumnsMatch &&
        rowCellCounts.length
      ) {
        console.warn(
          `[CustomTable] ${
            typeof title === "string" ? `"${title}" ` : ""
          }header/cell mismatch — layout rules disabled.`,
          { expectedColumns, rowCellCounts: Array.from(new Set(rowCellCounts)) }
        );
      }

      if (width !== lastWidth) {
        lastWidth = width;
        wrapper.style.setProperty("--action-column-width", width);
      }
      if (headerOffset !== lastHeaderOffset) {
        lastHeaderOffset = headerOffset;
        wrapper.style.setProperty("--header-offset", headerOffset);
      }
      if (overflowing !== lastOverflowing) {
        lastOverflowing = overflowing;
        wrapper.dataset.overflowing = overflowing;
      }
      if (columnsMatch !== lastColumnsMatch) {
        lastColumnsMatch = columnsMatch;
        table.dataset.columnsMatch = columnsMatch;
        // The edge overlay lives on the wrapper, so it needs the flag too —
        // otherwise it would draw a divider for a column that isn't pinned.
        wrapper.dataset.columnsMatch = columnsMatch;
      }
    };
    sync();

    // Coalesce bursts into one frame: resizing fires this repeatedly, and every
    // run reads layout.
    let frame: number | undefined;
    const observer = new ResizeObserver(() => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = undefined;
        sync();
      });
    });
    observer.observe(scroller);
    observer.observe(table);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
    // Primitive deps only — an array identity here would tear down and rebuild
    // the observer on every render. The row count is included because the
    // header/cell reconciliation above can only run once real rows exist, and a
    // table that loads its rows without ever resizing would otherwise never be
    // re-checked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerKey, actionColumnPosition, rowData?.length, emitsLayoutRules]);

  return (
    <CardContainer
      sx={{
        padding: "2rem",
        display: "flex",
        maxWidth: "100%",
        width: "100%",
        ...cardStyles,
      }}
      shadow={!disableShadow}
      secondaryColor={type === "secondary"}
    >
      <Box
        sx={{
          width: "100%",
          overflow: "hidden",
          maxWidth: "100%",
          height: "100%",
        }}
      >
        {isCheck && numSelected > 0 ? (
          <Box sx={{ display: "flex", justifyContent: "flex-end", pb: 1 }}>
            <Button onClick={() => setSelected([])}>Clear All Selection</Button>
            {deleteButtonName && (
              <CustomLoadingButton
                variant={buttonVariant}
                onClick={() => {
                  deleteButtonFunction(selected);
                  setSelected([]);
                }}
                isLoading={isLoadingDelete ?? false}
                title={deleteButtonName}
              />
            )}
          </Box>
        ) : (
          addButtonName && (
            <Button variant={buttonVariant} onClick={() => addButtonFunction()}>
              {addButtonName}
            </Button>
          )
        )}
        {(title || buttonName || isCheck || toolbarContent || tableId || edit) && (
          <EnhancedTableToolbar
            toolbarContent={toolbarContent}
            viewToggle={
              tableId || edit ? (
                <>
                  {/* The picker goes, the view toggle stays. Not being able to
                      hide the column you are editing is the point; not being
                      able to switch OUT of Compact — the view where a field is
                      most cramped — would just be a trap. */}
                  {columnPicker && !isEditing && (
                    <ColumnPicker
                      // The PERMITTED headers, not the visible ones: a hidden
                      // column has to stay in its own picker or there is no way
                      // to bring it back.
                      headers={permittedHeaders}
                      // The EFFECTIVE hidden list, not the stored one. Before
                      // the user's first choice these differ — the seed is in
                      // force while the record is still empty — and reading the
                      // record alone drew a seeded column ticked while it was
                      // absent from the table.
                      hidden={chosenColumns}
                      lockedIds={lockedColumnIds}
                      sortedHiddenColumn={sortedHiddenColumn}
                      onClearSort={() => setOrderBy("")}
                      onChange={(next) =>
                        setPreferences({ hiddenColumns: next })
                      }
                    />
                  )}
                  {edit && !isEditing && (
                    <PermissionGuard
                      {...(editPermission ?? { permissionType: "update" })}
                      byPassPermission={!editPermission}
                    >
                      <TableEditToggle onClick={edit.enter} />
                    </PermissionGuard>
                  )}
                  {tableId && (
                    <TableViewToggle
                      value={view}
                      onChange={(next) => setPreferences({ view: next })}
                    />
                  )}
                </>
              ) : undefined
            }
            buttonName={buttonName}
            ButtonIcon={ButtonIcon}
            buttonFunction={buttonFunction}
            buttonDisabled={buttonDisabled}
            buttonLoading={buttonLoading}
            primaryButtonPermission={primaryButtonPermission}
            secondaryButtonName={secondaryButtonName}
            ButtonIconSecondary={ButtonIconSecondary}
            secondaryButtonFunction={secondaryButtonFunction}
            tertiaryButtonName={tertiaryButtonName}
            TertiaryButtonIcon={TertiaryButtonIcon}
            tertiaryButtonFunction={tertiaryButtonFunction}
            tertiaryButtonDisabled={tertiaryButtonDisabled}
            tertiaryButtonLoading={tertiaryButtonLoading}
            showButton={showButton}
            buttonVariant={buttonVariant}
            searchField={searchField}
            title={title}
            numSelected={numSelected}
            rowCount={rowData?.length}
            onSelectAllClick={handleSelectAllClick}
            handleDelete={handleDelete}
            selected={selected}
            isHeader={isHeader}
            isCheck={isCheck}
            isAllSelected={isAllSelected}
            selectedActionObject={selectedActionObject}
            editing={edit ?? undefined}
          />
        )}

        <Box
          ref={scrollWrapperRef}
          sx={{
            position: "relative",
            // One continuous edge for the pinned column, drawn on the container
            // rather than on each cell. Per-cell shadows break at every row gap,
            // which reads as a stack of separate tabs instead of one layer.
            ...(actionColumnPosition > 0 && {
              "&::after": {
                content: '""',
                position: "absolute",
                // Start at the header rather than the container, so the edge does
                // not begin in the padding above it with a visible notch.
                top: "var(--header-offset, 0px)",
                bottom: 0,
                right: "var(--action-column-width, 0px)",
                // A hairline does the separating; the gradient only hints at depth.
                // A wide, heavier gradient on its own read as a grey smear across
                // the header instead of an edge.
                width: "10px",
                borderRight: `1px solid ${theme.palette.divider}`,
                pointerEvents: "none",
                opacity: 0,
                transition: "opacity 150ms ease",
                background:
                  "linear-gradient(to left, rgba(15, 23, 42, 0.05), rgba(15, 23, 42, 0))",
              },
              // Only once something is actually hidden underneath it, and only
              // while the column really is pinned — otherwise it reads as a
              // stray divider on a table that fits or one that opted out.
              '&[data-overflowing="true"][data-columns-match="true"]::after': {
                opacity: 1,
              },
            }),
          }}
        >
          <Box sx={{ overflow: "auto" }}>
            <Table
              ref={tableRef}
              // Optimistic default so the common case doesn't render a frame of
              // auto layout before sync() confirms; sync clears it when the
              // rendered cells don't line up with the headers.
              data-columns-match="true"
              sx={{
                minWidth: tableMinimumWidth ?? "800px",
                maxWidth: "100%",
                overflow: "auto",
                "-webkit-border-horizontal-spacing": "0",
                "-webkit-border-vertical-spacing": "1em",
                "border-collapse": "separate",
                ...layoutSx,
              }}
              aria-label="customized table"
              size="small"
            >
              {isHeader && (
                <EnhancedTableHead
                  headCells={visibleHeaders}
                  orderBy={orderBy}
                  order={order}
                  onSelectAllClick={handleSelectAllClick}
                  rowCount={rowData?.length}
                  onRequestSort={handleRequestSort}
                  numSelected={selected.length}
                  isCheck={isCheck}
                  toolTipProps={toolTipProps}
                  isToolTip={isToolTip}
                  tableHeaderStyles={tableHeaderStyles}
                  // Sorting is frozen while editing. `sortRows` orders
                  // `rowData`, which holds the SAVED values — so a sort by an
                  // edited column would either ignore what the user just typed
                  // or move the row out from under the cursor. Neither is worth
                  // offering; the order simply stays as it was on entry.
                  isEditing={isEditing}
                />
              )}
              <TableBody sx={{ borderRadius: BORDER_RADIUS }}>
                {/* Only the rows can hold an <ActionCell />, and a context
                    provider renders no DOM of its own, so scoping it here keeps
                    the `<table>` structure exactly as it was. */}
                <ColumnsAreHideableContext.Provider value={columnsAreHideable}>
                  <VisibleColumnsContext.Provider value={visibleColumnIds}>
                    {/* The value here carries the draft STORE, not the drafts.
                        A Map in the value would invalidate every consumer on
                        every keystroke, whichever row changed — which is the
                        cost this whole design exists to avoid. */}
                    <TableEditContext.Provider
                      value={edit?.contextValue ?? null}
                    >
                    <ActionColumnContext.Provider value={actionColumnVisible}>
                      {isLoading ? (
                        <>
                          {[
                            ...Array(noOfItemsLoading ?? rowsPerPage).keys(),
                          ].map((x, i) => (
                            <TableRowPlaceholder
                              key={i}
                              type={type}
                              headerLength={visibleHeaders.length ?? 1}
                            />
                          ))}
                        </>
                      ) : Object.keys(rowData).length <= 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={visibleHeaders.length}
                            align="center"
                          >
                            <h1>{emptyTableText ?? messages.noDataFound}</h1>
                          </TableCell>
                        </TableRow>
                      ) : (
                        <>
                          {sortedRows.map((row, index) => {
                            const rowKey =
                              getRowKey?.(row, index) ?? row?.id ?? index;
                            return (
                              <StyledTableRow
                                key={rowKey}
                                secondarytype={
                                  type === "secondary" ? "true" : "false"
                                }
                                sx={tableRowStyles}
                                onClick={
                                  // The handler is on the whole row, so in edit
                                  // mode a click meant for an input would
                                  // navigate away and take the drafts with it.
                                  onRowClick && !isEditing
                                    ? () => onRowClick?.(row)
                                    : undefined
                                }
                              >
                                <TableRowComponent
                                  row={row}
                                  {...onCheckTableRowProps}
                                  {...rowComponentProps}
                                />
                              </StyledTableRow>
                            );
                          })}
                        </>
                      )}
                    </ActionColumnContext.Provider>
                    </TableEditContext.Provider>
                  </VisibleColumnsContext.Provider>
                </ColumnsAreHideableContext.Provider>
              </TableBody>
            </Table>
          </Box>
        </Box>
      </Box>
      <Box>
        {pagination && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 100]}
            component="div"
            count={countRef.current ?? rowData?.length ?? -1}
            rowsPerPage={rowsPerPage}
            page={page}
            // Drafts belong to the rows on screen, and leaving the page would
            // strand them: `prune` drops a draft whose row is gone. Blocked
            // while editing rather than while dirty — the arrows are DISABLED,
            // not silently no-op'd, because a control that does nothing when
            // clicked reads as a bug rather than as a rule.
            onPageChange={isEditing ? () => null : handleChangePage}
            onRowsPerPageChange={
              isEditing ? () => null : handleChangeRowsPerPage
            }
            backIconButtonProps={isEditing ? { disabled: true } : undefined}
            nextIconButtonProps={isEditing ? { disabled: true } : undefined}
            SelectProps={isEditing ? { disabled: true } : undefined}
            sx={{ overflow: "hidden" }}
          />
        )}
      </Box>
    </CardContainer>
  );
}

type TableStyle = InputFieldType;

interface CustomTableProps<TDATA> extends ITableButton, ITablePaginaion {
  title?: string | React.ReactElement;
  toolbarContent?: React.ReactNode;
  headers: Array<ITableHeaders>;
  rowData: Array<TableRowData | TDATA>;
  tableRowComponent?: any;
  isCheck?: boolean;
  handleDelete?: any;
  tableRowComponentProps?: any;
  /**
   * Which column the table opens sorted by. `-1` opens it unsorted, in whatever
   * order `rowData` arrived in.
   */
  sortByIndex?: number;
  sortBy?: Order;
  /**
   * Hand the sort to the caller instead of sorting the rows here.
   *
   * Client-side sorting only ever reaches the rows the table was given, which on
   * a server-paginated list is one page. Passing this switches local sorting OFF
   * and reports each change, so the list can put `sortBy` / `sortOrder` on its
   * query and let the API order the whole result set.
   *
   * `columnId` is the header id; look up that header's `sortKey` if the API
   * field differs from the column id. Reset the page to 0 when it fires —
   * staying on page 7 of a set that just reordered shows rows the user never
   * asked for.
   *
   * No API in `src/apis/` accepts sort parameters yet, so nothing passes this
   * today. It exists so that enabling a column is a change to one list rather
   * than to the table.
   */
  onSortChange?: (sort: { columnId: string; order: Order }) => void;
  type?: TableStyle;
  tableMinimumWidth?: string;
  isHeader?: boolean;
  isLoading?: boolean;
  // Widened to the element type of `rowData`. It used to claim
  // `{ [x: string]: string | number }`, which `TableRowData` does not satisfy —
  // that allows `string[]` values too. Nothing caught it while the rows reached
  // here through `stableSort`, whose `Array<any>` parameter erased the type on
  // the way past.
  onRowClick?: (row: TableRowData | TDATA) => void;
  /**
   * React key for a row, when `row.id` is not unique across the list.
   *
   * The default is `row.id`, which is fine for a list drawn from one table. It
   * is NOT fine for one that merges several: the in-campaign line items come
   * from three tables with independent id sequences, so a Beeswax and a LinkedIn
   * line item can share an id — React then logs "two children with the same key"
   * and duplicates or drops one of them. Sorting makes it worse, because rows
   * with equal keys reorder unpredictably.
   */
  getRowKey?: (row: any, index: number) => string | number;
  selectedActionObject?: SelectedAction[];
  isClearSelected?: boolean;
  setIsClearSelected?: React.Dispatch<React.SetStateAction<boolean>>;
  disableShadow?: boolean;
  noOfItemsLoading?: number;
  tableRowStyles?: SxProps<Theme>;
  isToolTip?: boolean;
  toolTipProps?: TableToolTipProps;
  cardStyles?: SxProps;
  tableHeaderStyles?: SxProps;
  searchField?: SearchField;
  emptyTableText?: string;
  /**
   * Pins the action column to the right edge while the rest of the row scrolls
   * horizontally. On by default; pass `false` to opt a table out. Automatically
   * skipped when the table declares no action column, or when any header spans
   * multiple columns. Opting out does not affect truncation — the action column
   * stays exempt from the ellipsis rules either way.
   */
  stickyActions?: boolean;
  /**
   * How a cell handles content wider than its column. The three user-facing
   * views map onto this plus `density`:
   *
   * - `truncate` — one line, clipped with an ellipsis, revealed on hover by the
   *   app-level `<OverflowTooltip />`. With `density="compact"` this is the
   *   **Compact** view.
   * - `wrap` — keeps the declared widths and spends overflow on height. Only
   *   columns marked `wrap` in the header actually wrap; see `ITableHeaders`.
   *   With `maxLines` this is the **Full** view.
   * - `fit` — columns size to their content and the table scrolls sideways
   *   inside its card, one line per cell, action column still pinned. The only
   *   view where nothing is ever hidden. This is the **Fit** view.
   *
   * `fit` ignores declared widths outright, so `width` and `grow` do nothing
   * there — it is a second layout regime, and worth re-checking whenever the
   * table's CSS changes.
   *
   * Ignored, along with `density` and `maxLines`, when the table names a
   * `tableId` — the named view decides all three then. Pass these directly only
   * to render a setting that isn't one of the three views.
   */
  cellDisplay?: TableCellDisplay;
  /**
   * Cell padding and font size. `compact` fits more columns before anything has
   * to be clipped or hidden; `comfortable` is today's size. See `cellDisplay`
   * for how this interacts with `tableId`.
   */
  density?: TableDensity;
  /**
   * With `cellDisplay="wrap"`, cap a cell at this many lines and ellipsise the
   * rest. Bounds the row height that plain wrapping gives away. Columns marked
   * `noTruncate`, and the action column, are exempt.
   */
  maxLines?: number;
  /**
   * Turns on the Compact / Full / Fit toggle, and names the record the choice is
   * remembered under. Stable and unique per list — not derived from the headers
   * or the URL, both of which change under you (see `tablePreferences`).
   *
   * Naming one is the whole opt-in: it decides the toggle, the persistence, and
   * (with `defaultView`) the layout, so a list can't end up with a toggle that
   * forgets or a remembered view nothing reads.
   */
  tableId?: string;
  /**
   * Which view a list opens in before the user picks one. `compact` — one line
   * per row — unless the list has been through the wrapping sweep, since `full`
   * only wraps columns whose header is marked `wrap` and would otherwise just
   * render as today with taller padding.
   */
  defaultView?: TableView;
  /**
   * Column ids to leave out. Drops the header AND the cell together — the cell
   * only if the row renders it through `<Cell columnId />`, so a row that still
   * uses `StyledTableCell` will desync. Convert the row first.
   *
   * For driving the choice by hand. Ignored when `columnPicker` is on, where the
   * remembered choice is the only owner.
   */
  hiddenColumns?: string[];
  /**
   * Renders the column picker beside the view toggle, and remembers the choice
   * with the rest of this table's preferences. Needs a `tableId`, and needs the
   * row converted to `<Cell columnId />` on every column — a header that
   * disappears while its `<td>` keeps rendering puts every column-position rule
   * one column out.
   *
   * For the wide tables. On a five-column list there is nothing worth hiding,
   * and the button costs more toolbar than it earns.
   */
  columnPicker?: boolean;
  /**
   * Turns on edit mode: an Edit button in the toolbar, and the named columns
   * become fields on every loaded row until the user saves or discards.
   *
   * Pass CONFIG, not state. The drafts are held here, in a ref, and each cell
   * subscribes to its own — a keystroke re-renders one cell rather than this
   * table and all of its rows. A list that owned the drafts would re-render
   * both on every character; see the note at the top of `../editing`.
   *
   * The list keeps exactly one job: `onSave`. Mark each editable column
   * `editable` in its header too — that is what the layout rules read.
   *
   * Rows must render `<EditableCell />` for the named columns. A row that
   * doesn't simply stays read-only; it will not desync, because `EditableCell`
   * renders one `<td>` either way.
   */
  editing?: TableEditingConfig<TDATA>;
  /**
   * Who may turn edit mode on. Same shape as `primaryButtonPermission`. Omit it
   * and the button shows for everyone — pass the module's `update` to match the
   * action column.
   */
  editPermission?: PermissionGuardExternalProps;
}

function TableRowPlaceholder({ type, headerLength }: any) {
  return (
    <StyledTableRow secondarytype={type === "secondary" ? "true" : "false"}>
      <StyledTableCell colSpan={headerLength}>
        <TableItemPlaceHolder />
      </StyledTableCell>
    </StyledTableRow>
  );
}
